import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import ListItemText from '@mui/material/ListItemText';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';

import { paths } from 'src/routes/paths';

import Iconify from 'src/components/iconify';

import { useActiveAgencyBranding } from 'src/sections/portal/hooks/useActiveAgencyBranding';
import { createPortalTheme, PORTAL_DISPLAY_FONT } from 'src/sections/portal/utils/create-portal-theme';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

// The portal is the client's whole relationship with the agency (PRD #79), not a Locker app —
// nav is Spanish-first and the Locker ("Mi casillero") is one module among many (issue #91).
const NAV_ITEMS = [
  { label: 'Inicio', path: paths.portal.home, icon: 'solar:home-2-bold-duotone' },
  { label: 'Mis envíos', path: paths.portal.shipments, icon: 'solar:delivery-bold-duotone' },
  { label: 'Mis pasaportes', path: paths.portal.passports, icon: 'solar:passport-bold-duotone' },
  { label: 'Mis remesas', path: paths.portal.remittances, icon: 'solar:dollar-bold-duotone' },
  { label: 'Mis trámites', path: paths.portal.migrations, icon: 'solar:document-text-bold-duotone' },
  { label: 'Mi casillero', path: paths.portal.packages, icon: 'solar:box-bold-duotone' },
];

// Neutral Spanish fallback when an agency has no branding name — never "Your Locker".
const PORTAL_FALLBACK_NAME = 'Portal del cliente';

// Agency logo (image) with a brand-colored letter fallback.
function AgencyMark({ name, logoUrl }: { name?: string; logoUrl?: string | null }) {
  if (logoUrl) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={name}
        sx={{ height: 36, maxWidth: 160, objectFit: 'contain' }}
      />
    );
  }
  return (
    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 700 }}>
      {name?.charAt(0)?.toUpperCase()}
    </Avatar>
  );
}

export default function PortalLayout({ children }: Props) {
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  const { branding, palette } = useActiveAgencyBranding();
  const theme = createPortalTheme(palette);

  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);

  const currentNav = NAV_ITEMS.findIndex((item) => location.pathname.startsWith(item.path));
  const agencyName = branding?.name ?? PORTAL_FALLBACK_NAME;
  const handleLogout = () => logout({ logoutParams: { returnTo: window.location.origin } });

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Toolbar sx={{ gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <AgencyMark name={agencyName} logoUrl={branding?.logoUrl} />
              <Typography
                sx={{
                  fontFamily: PORTAL_DISPLAY_FONT,
                  fontWeight: 600,
                  fontSize: 18,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {agencyName}
              </Typography>
            </Box>

            {/* desktop nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, ml: 3, flexGrow: 1 }}>
              {NAV_ITEMS.map((item, i) => {
                const active = i === currentNav;
                return (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    startIcon={<Iconify icon={item.icon} width={20} />}
                    sx={{
                      px: 1.5,
                      color: active ? 'primary.main' : 'text.secondary',
                      fontWeight: active ? 700 : 500,
                      borderRadius: 1,
                      bgcolor: active ? 'primary.lighter' : 'transparent',
                      '&:hover': { bgcolor: 'primary.lighter' },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

            {user && (
              <>
                <IconButton onClick={(e) => setMenuEl(e.currentTarget)} sx={{ p: 0.5 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 15 }}>
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={menuEl}
                  open={Boolean(menuEl)}
                  onClose={() => setMenuEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {user.name || 'Sesión iniciada'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {user.email}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <Iconify icon="solar:logout-3-bold-duotone" width={20} sx={{ mr: 1.5 }} />
                    <ListItemText>Cerrar sesión</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: { xs: 2, sm: 3 },
            pb: { xs: 10, md: 3 },
          }}
        >
          {children}
        </Box>

        {/* mobile bottom nav */}
        <BottomNavigation
          showLabels
          value={currentNav >= 0 ? currentNav : 0}
          onChange={(_, v) => navigate(NAV_ITEMS[v].path)}
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            '& .Mui-selected': { color: 'primary.main' },
          }}
        >
          {NAV_ITEMS.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={<Iconify icon={item.icon} width={24} />}
            />
          ))}
        </BottomNavigation>
      </Box>
    </ThemeProvider>
  );
}
